/*
 * Data Display Components for Grippy AI
 * 
 * Components for displaying tables and charts returned from the API.
 * 
 * This file
 * - Displays a table and a chart
 * - The table is displayed in a table component
 * - The chart is displayed in a chart component
 * Table component has: rename, download, copy to clipboard, sort, pagination, and table data
 * Chart component has: rename, download, copy to to clipboard, change color, chart data
*/
import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useTheme } from '../ThemeContext.tsx';
import { Download, Pencil, Copy, Palette, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import * as XLSX from "xlsx";
import Plot from 'react-plotly.js';
import { toast } from 'sonner';
import { SortState, sortTableData, handleColumnSort } from '../utils/sortUtils.ts';
import { HexColorPicker } from 'react-colorful';
import {
  applyBrandColorsToFigure,
  applyTraceOverride,
  buildColorscale,
  buildPointColors,
  chartPaletteColors,
  getTraceColorCaps,
  pickDefaultMode,
  TraceOverride,
} from '../utils/applyColors.ts';
// Utility function to convert data URL to Blob
const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

// Check if ClipboardItem is supported
const isClipboardItemSupported = (): boolean => {
  return typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';
};



interface TableData {
  title: string;
  columns: string[];
  data: any[];
  row_count: number;
}

interface ChartData {
  title: string;
  type: 'plotly' | 'matplotlib';
  data: string;
  chart_id: string;
}

interface DataTableProps {
  table: TableData;
}

export const DataTable: React.FC<DataTableProps> = ({ table }) => {
  const { colors, isMobile } = useTheme();
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  const [isCopying, setIsCopying] = useState(false);

  // only use 320 px width for mobile screen to make the table more readable
  const mobileContainerPx = 320;
  // the minimum width of the table should be the width of the container plus 160 px to make the table more readable
  const mobileMinWidth = Math.max(mobileContainerPx + 160, (table.columns.length + 1) * 120);

  // Reset page and sort state when table data changes
  useEffect(() => {
    setCurrentPage(1);
    setSortState({ column: null, direction: null });
  }, [table.data]);

  // Sort data before pagination
  const sortedData = useMemo(
    () => sortTableData(table.data, sortState),
    [table.data, sortState]
  );

  const totalRows = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const hasPagination = totalPages > 1;

  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRows = useMemo(
    () => sortedData.slice(startIndex, startIndex + rowsPerPage),
    [sortedData, startIndex]
  );

  // Handle column header click for sorting
  const handleSort = useCallback((column: string) => {
    setSortState(prev => handleColumnSort(prev, column));
    setCurrentPage(1); // Reset to first page when sorting changes
  }, []);

  // Render sort indicator icon
  const renderSortIcon = (column: string) => {
    if (sortState.column !== column) {
      return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />;
    }
    if (sortState.direction === 'asc') {
      return <ChevronUp className="w-3.5 h-3.5" />;
    }
    if (sortState.direction === 'desc') {
      return <ChevronDown className="w-3.5 h-3.5" />;
    }
    return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />;
  };

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const showingStart = totalRows === 0 ? 0 : startIndex + 1;
  const showingEnd = Math.min(totalRows, startIndex + paginatedRows.length);

  // handle download table as xlsx file
  const handleDownload = () => {

    const worksheet = XLSX.utils.json_to_sheet(table.data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "table_data.xlsx");
  };

  // handle copy table to clipboard with formatted data
  const handleCopyToClipboard = async () => {
    if (isCopying) return;
    setIsCopying(true);

    try {
      // Use sortedData to copy all data (not just paginated rows)
      const dataToCopy = sortedData;

      // Helper function to format cell value for TSV/Excel
      // Preserves numeric format and handles comma/dot separators correctly
      const formatCellValue = (value: any): string => {
        if (value === null || value === undefined) {
          return '';
        }

        // If it's already a number, convert to string preserving the format
        if (typeof value === 'number') {
          return String(value);
        }

        // If it's a string that looks like a number, preserve it as-is
        // This handles cases like "1,234.56" or "1.234,56" (European format)
        const stringValue = String(value);

        // Check if it's a numeric string (may contain comma or dot as separators)
        // Don't modify numeric strings - Excel will parse them correctly
        return stringValue;
      };

      // Format as TSV (Tab-Separated Values) - Excel prefers this format
      const tsvRows: string[] = [];

      // Add header row (tab-separated)
      tsvRows.push(table.columns.map(col => {
        const value = String(col);
        // Replace tabs and newlines in headers
        return value.replace(/\t/g, ' ').replace(/\n/g, ' ').replace(/\r/g, '');
      }).join('\t'));

      // Add data rows (tab-separated)
      dataToCopy.forEach(row => {
        const tsvRow = table.columns.map(column => {
          const value = row[column];
          const formattedValue = formatCellValue(value);
          // Replace tabs and newlines in cell values (replace with space to preserve readability)
          return formattedValue.replace(/\t/g, ' ').replace(/\n/g, ' ').replace(/\r/g, '');
        }).join('\t');
        tsvRows.push(tsvRow);
      });

      const tsvContent = tsvRows.join('\n');

      // Format as HTML table for rich text editors (Teams, Word, etc.)
      const htmlTable = `
        <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; font-family: Arial, sans-serif;">
          <thead>
            <tr style="background-color: #f3f4f6; font-weight: bold;">
              ${table.columns.map(col => `<th>${String(col).replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&/g, '&amp;')}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${dataToCopy.map(row => `
              <tr>
                ${table.columns.map(column => {
        const value = row[column];
        const formattedValue = formatCellValue(value);
        return `<td>${formattedValue.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&/g, '&amp;')}</td>`;
      }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `.trim();

      // Try to use modern Clipboard API with ClipboardItem
      if (isClipboardItemSupported()) {
        try {
          // Use TSV as plain text - Excel will parse this correctly into cells
          const tsvBlob = new Blob([tsvContent], { type: 'text/plain' });
          const htmlBlob = new Blob([htmlTable], { type: 'text/html' });

          // Create a CSV version as well for compatibility
          // Convert TSV to CSV by replacing tabs with commas (but keep numbers as-is)
          const csvContent = tsvContent.replace(/\t/g, ',');
          const csvBlob = new Blob([csvContent], { type: 'text/csv' });

          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': tsvBlob,  // Primary format - Excel prefers TSV
              'text/html': htmlBlob,   // For rich text editors
              'text/csv': csvBlob      // CSV fallback
            })
          ]);

        } catch (clipboardError) {
          console.error('Clipboard write failed:', clipboardError);
          // Fallback to plain text TSV
          await navigator.clipboard.writeText(tsvContent);
        }
      } else {
        // Fallback for browsers that don't support ClipboardItem
        // Use TSV format which Excel will parse correctly
        await navigator.clipboard.writeText(tsvContent);
        toast.success('Table data copied to clipboard!', {
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error copying table to clipboard:', error);
      toast.error('Failed to copy table data. Please try again.');
    } finally {
      setIsCopying(false);
    }
  };

  const isComparisonColumn = (columnName: string): boolean => {
    const s = String(columnName || '').toLowerCase();
    // We only color true *delta* columns (gap/variance/diff) or explicit comparisons (vs/against ...)
    // to avoid coloring raw target columns like "(AP Value)" / "(AP RunRate)".
    const hasDeltaWord =
      s.includes('gap') ||
      s.includes('variance') ||
      s.includes('delta') ||
      s.includes('diff') ||
      s.includes('%') ||
      s.includes('vs ')
    s.includes(' vs')
    s.includes('target')
    s.includes('difference');

    const hasVsWord = /\b(vs|versus|against)\b/.test(s);
    const hasTargetContext = /\b(target|plan|ap)\b/.test(s);

    return hasDeltaWord || (hasVsWord && hasTargetContext);
  };

  const parseSignedNumber = (v: any): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const raw = String(v).trim();
    if (!raw) return null;

    // Keep a leading sign if present, drop common units and formatting
    const cleaned = raw
      .replace(/[,%]/g, '')
      .replace(/\b(pp|pts?|bps)\b/gi, '')
      .replace(/,/g, '')
      .trim();

    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const getComparisonValueClass = (columnName: string, value: any): string => {
    if (!isComparisonColumn(columnName)) return '';
    const n = parseSignedNumber(value);
    if (n === null || n === 0) return '';
    return n > 0 ? 'text-[#13670B] font-semibold' : 'text-[#FF2B00] font-semibold';
  };

  return (
    <div className={`w-full max-w-4xl ${colors.bg.secondary} rounded-lg border ${colors.border.primary} ${isMobile ? 'p-3' : 'p-4'} mb-4 mx-auto`}>
      <div className="w-full">
        <div className={`mb-3 flex justify-end items-center gap-2`}>
          {/* copy to clipboard button */}
          <button
            onClick={handleCopyToClipboard}
            disabled={isCopying}
            className={`px-3 py-1 rounded-md border ${colors.border.primary} ${colors.bg.secondary} ${colors.interactive.secondaryHover} text-sm disabled:opacity-50 disabled:cursor-wait `}
            title="Copy table to clipboard"
            aria-label="Copy table to clipboard"
          >
            {isCopying ? (
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            ) : (
              <Copy className='w-5 h-5' />
            )}
          </button>
          {/* download button to download a real excel file to view full information of the table */}
          <button
            onClick={handleDownload}
            className={`px-3 py-1 rounded-md border ${colors.border.primary} ${colors.bg.secondary} ${colors.interactive.secondaryHover} text-sm disabled:opacity-50 disabled:cursor-wait`}

          >
            <Download className='w-5 h-5' />
          </button>
        </div>

        <div
          className="w-full overflow-x-auto scroll-smooth"
        >
          <table
            className={`min-w-full ${isMobile ? 'text-xs' : 'text-sm'} ${colors.text.secondary} border-collapse`}
          >
            <thead>
              <tr className={`${colors.bg.tertiary} border-b ${colors.border.primary}`}>
                {table.columns.map((column, index) => (
                  <th
                    key={index}
                    className={`text-left ${isMobile ? 'p-2' : 'p-3'} font-medium ${colors.text.primary} cursor-pointer select-none transition-colors`}
                    onClick={() => handleSort(column)}
                    title={`Click to sort by ${column}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{column}</span>
                      {renderSortIcon(column)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`border-b ${colors.border.primary} hover:${colors.bg.tertiary}`}
                >
                  {table.columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`${isMobile ? 'p-2' : 'p-3'} ${getComparisonValueClass(column, row[column])}`}
                    >
                      {row[column] !== null && row[column] !== undefined
                        ? String(row[column])
                        : "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasPagination && (
        <div className={`mt-4 flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'}`}>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} ${colors.text.tertiary}`}>
            Showing {showingStart}-{showingEnd} of {table.row_count ?? totalRows} rows
          </p>
          <div className={`flex items-center gap-3 ${isMobile ? 'w-full' : ''}`}>
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md border ${colors.border.primary} ${colors.bg.tertiary} ${isMobile ? 'flex-1' : ''
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Previous
            </button>
            <span className={`text-sm font-medium ${colors.text.secondary} ${isMobile ? 'text-center flex-1' : ''}`}>
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md text-white ${colors.interactive.primary} ${colors.interactive.primaryHover} ${isMobile ? 'flex-1' : ''
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface DataChartProps {
  chart: ChartData;
  summaryText?: string;
}

export const DataChart: React.FC<DataChartProps> = ({ chart, summaryText }) => {
  const plotlyGraphRef = useRef<any>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const { colors, isMobile } = useTheme();
  const mobileChartContainer = 300;
  const mobileChartMinWidth = 530;
  const baseMargins = isMobile
    ? { t: 60, r: 24, b: 80, l: 60 }
    : { t: 80, r: 40, b: 100, l: 100 };

  const [isSharing, setIsSharing] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [tempTitle, setTempTitle] = useState('');
  const [selectedTraceIndices, setSelectedTraceIndices] = useState<number[]>([]);
  const [traceColorOverrides, setTraceColorOverrides] = useState<Record<number, TraceOverride>>({});
  const [selectedPoints, setSelectedPoints] = useState<Array<{ traceIndex: number; pointIndex: number }>>([]);
  const [pointColorOverrides, setPointColorOverrides] = useState<Record<number, Record<number, string>>>({});
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [pickerPreviewHex, setPickerPreviewHex] = useState<string | null>(null);
  const [customPickerHex, setCustomPickerHex] = useState('#13670B');

  // Reset state when chart data changes (new chart generated)
  useEffect(() => {
    setCustomTitle(null);
    setIsEditingTitle(false);
    setTempTitle('');
    setSelectedTraceIndices([]);
    setTraceColorOverrides({});
    setSelectedPoints([]);
    setPointColorOverrides({});
    setIsColorPickerOpen(false);
    setPickerPreviewHex(null);
    setCustomPickerHex('#13670B');
    // Clear the ref - it will be set again when new chart initializes
    plotlyGraphRef.current = null;
  }, [chart.data, chart.chart_id]);

  // Close color picker on outside click or Escape
  useEffect(() => {
    if (!isColorPickerOpen) return;
    const handle = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent) {
        if (e.key === 'Escape') setIsColorPickerOpen(false);
        return;
      }
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setIsColorPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', handle);
    };
  }, [isColorPickerOpen]);

  // Extract original title from chart data
  const getOriginalTitle = (): string => {
    try {
      const parsed = JSON.parse(chart.data);
      const titleObj = parsed.layout?.title;
      if (typeof titleObj === 'string') return titleObj;
      if (titleObj?.text) return titleObj.text;
      return chart.title || 'Chart';
    } catch {
      return chart.title || 'Chart';
    }
  };

  const currentTitle = customTitle ?? getOriginalTitle();

  const handleStartEditTitle = () => {
    setTempTitle(currentTitle);
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  const handleSaveTitle = () => {
    const newTitle = tempTitle.trim();
    if (newTitle && newTitle !== getOriginalTitle()) {
      setCustomTitle(newTitle);
    } else if (!newTitle || newTitle === getOriginalTitle()) {
      setCustomTitle(null); // Reset to original
    }
    setIsEditingTitle(false);
  };

  const handleCancelEdit = () => {
    setTempTitle(currentTitle); // Reset to current title
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Helper function to wait for Plotly ref with retries and DOM fallback
  const waitForPlotlyRef = async (maxRetries = 5, interval = 150): Promise<any | null> => {
    for (let i = 0; i < maxRetries; i++) {
      // Check the ref first
      if (plotlyGraphRef.current) {
        return plotlyGraphRef.current;
      }

      // Fallback: try to find the Plotly graph element in the DOM
      // Plotly creates elements with class 'js-plotly-plot'
      const plotlyElements = document.querySelectorAll('.js-plotly-plot');
      if (plotlyElements.length > 0) {
        const gd = plotlyElements[plotlyElements.length - 1] as any;
        // Verify it's a valid Plotly element with data
        if (gd && gd.data && gd.layout) {
          plotlyGraphRef.current = gd;
          return gd;
        }
      }

      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    return null;
  };

  const normalizeHexInput = (raw: string): string => {
    let s = raw.trim();
    if (!s) return '';
    if (!s.startsWith('#')) s = `#${s}`;
    // allow user to type progressively; cap length reasonably
    if (s.length > 7) s = s.slice(0, 7);
    return s;
  };

  const isValidHex = (s: string): boolean => /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(s.trim());

  const applyColorToSelection = async (hexRaw: string) => {
    const hex = normalizeHexInput(hexRaw);
    setCustomPickerHex(hex);
    if (!isValidHex(hex)) return; // allow free typing; only apply when valid

    // Update React state overrides
    if (selectedPoints.length > 0) {
      const grouped: Record<number, number[]> = {};
      selectedPoints.forEach((p) => {
        (grouped[p.traceIndex] ||= []).push(p.pointIndex);
      });
      setPointColorOverrides((prev) => {
        const next = { ...prev };
        Object.entries(grouped).forEach(([tIdx, pts]) => {
          const ti = Number(tIdx);
          const cur = { ...(next[ti] || {}) };
          pts.forEach((pi) => { cur[pi] = hex; });
          next[ti] = cur;
        });
        return next;
      });
    } else if (selectedTraceIndices.length > 0) {
      const update: Record<number, TraceOverride> = {};
      selectedTraceIndices.forEach((idx) => { update[idx] = { baseHex: hex, mode: 'auto' }; });
      setTraceColorOverrides((prev) => ({ ...prev, ...update }));
    }

    // Apply to Plotly immediately
    const gd = await waitForPlotlyRef(2, 80);
    const PlotlyLib = (window as any).Plotly;
    if (!gd || !PlotlyLib?.restyle) return;

    // Point-level recolor
    if (selectedPoints.length > 0) {
      const grouped: Record<number, number[]> = {};
      selectedPoints.forEach((p) => {
        (grouped[p.traceIndex] ||= []).push(p.pointIndex);
      });

      const pointMarkerColorIndices: number[] = [];
      const pointMarkerColorValues: any[] = [];
      const pieMarkerColorsIndices: number[] = [];
      const pieMarkerColorsValues: any[] = [];

      Object.entries(grouped).forEach(([tIdx, pts]) => {
        const idx = Number(tIdx);
        const t = gd?.data?.[idx];
        const caps = getTraceColorCaps(t);
        const n = caps.pointCount ?? 0;
        if (n <= 0) return;
        const type = t?.type ?? 'scatter';

        const ensureArray = (existing: any): string[] => {
          if (Array.isArray(existing) && existing.length > 0) return existing.map((x: any) => String(x));
          if (typeof existing === 'string' && existing) return Array.from({ length: n }, () => existing);
          return buildPointColors(hex, n);
        };

        if (type === 'pie') {
          const arr = ensureArray(t?.marker?.colors);
          pts.forEach((pi) => { if (pi >= 0 && pi < arr.length) arr[pi] = hex; });
          pieMarkerColorsIndices.push(idx);
          pieMarkerColorsValues.push(arr);
        } else {
          const arr = ensureArray(t?.marker?.color);
          pts.forEach((pi) => { if (pi >= 0 && pi < arr.length) arr[pi] = hex; });
          pointMarkerColorIndices.push(idx);
          pointMarkerColorValues.push(arr);
        }
      });

      if (pointMarkerColorIndices.length > 0) {
        PlotlyLib.restyle(gd, { 'marker.color': pointMarkerColorValues }, pointMarkerColorIndices);
      }
      if (pieMarkerColorsIndices.length > 0) {
        PlotlyLib.restyle(gd, { 'marker.colors': pieMarkerColorsValues }, pieMarkerColorsIndices);
      }
      return;
    }

    // Trace-level recolor (auto-mode chooses best per trace)
    const traceModeIndices: number[] = [];
    const pointMarkerColorIndices: number[] = [];
    const pointMarkerColorValues: any[] = [];
    const pieMarkerColorsIndices: number[] = [];
    const pieMarkerColorsValues: any[] = [];
    const colorscaleIndices: number[] = [];
    const colorscaleValues: any[] = [];
    const coloraxisRelayout: Record<string, any> = {};

    selectedTraceIndices.forEach((idx) => {
      const t = gd?.data?.[idx];
      const caps = getTraceColorCaps(t);
      const resolved = pickDefaultMode(caps);
      const type = t?.type ?? 'scatter';

      if (resolved === 'colorscale') {
        const axisName = typeof t?.coloraxis === 'string' ? t.coloraxis : null;
        if (axisName) {
          coloraxisRelayout[`${axisName}.colorscale`] = buildColorscale(hex);
        } else {
          colorscaleIndices.push(idx);
          colorscaleValues.push(buildColorscale(hex));
        }
        return;
      }

      if (resolved === 'points') {
        const n = caps.pointCount ?? 0;
        const colorsArr = buildPointColors(hex, n);
        if (type === 'pie') {
          pieMarkerColorsIndices.push(idx);
          pieMarkerColorsValues.push(colorsArr);
        } else {
          pointMarkerColorIndices.push(idx);
          pointMarkerColorValues.push(colorsArr);
        }
        return;
      }

      traceModeIndices.push(idx);
    });

    if (traceModeIndices.length > 0) {
      const colorArr = traceModeIndices.map(() => hex);
      PlotlyLib.restyle(gd, { 'line.color': colorArr, 'marker.color': colorArr }, traceModeIndices);
    }
    if (pointMarkerColorIndices.length > 0) {
      PlotlyLib.restyle(gd, { 'marker.color': pointMarkerColorValues }, pointMarkerColorIndices);
    }
    if (pieMarkerColorsIndices.length > 0) {
      PlotlyLib.restyle(gd, { 'marker.colors': pieMarkerColorsValues }, pieMarkerColorsIndices);
    }
    if (colorscaleIndices.length > 0) {
      PlotlyLib.restyle(gd, { colorscale: colorscaleValues }, colorscaleIndices);
    }
    if (Object.keys(coloraxisRelayout).length > 0 && typeof PlotlyLib.relayout === 'function') {
      PlotlyLib.relayout(gd, coloraxisRelayout);
    }
  };

  const handleResetView = async () => {
    try {
      const gd = await waitForPlotlyRef(3, 100);
      if (!gd) {
        console.warn('Plotly graph div not available for reset');
        return;
      }

      const PlotlyLib = (window as any).Plotly;
      if (!PlotlyLib || typeof PlotlyLib.relayout !== 'function') {
        console.warn('Plotly library not available on window.Plotly for reset');
        return;
      }

      // Reset both axes to their original autorange
      await PlotlyLib.relayout(gd, {
        'xaxis.autorange': true,
        'yaxis.autorange': true,
      });
    } catch (error) {
      console.error('Error resetting Plotly chart view:', error);
    }
  };

  const handleDownloadPlotly = async () => {
    try {
      // Wait for chart to be fully rendered with retry logic
      const gd = await waitForPlotlyRef();
      if (!gd) {
        toast.error('Chart not ready. Please try again.');
        console.warn('Plotly graph div not available for download after retries');
        return;
      }

      const PlotlyLib = (window as any).Plotly;
      if (!PlotlyLib || typeof PlotlyLib.toImage !== 'function') {
        toast.error('Unable to capture chart.');
        console.warn('Plotly library not available on window.Plotly for download');
        return;
      }

      // Reset to original view before capturing (title is already visible in chart)
      await PlotlyLib.relayout(gd, {
        'xaxis.autorange': true,
        'yaxis.autorange': true,
      });

      // Wait for relayout to complete rendering
      await new Promise(resolve => setTimeout(resolve, 150));

      const dataUrl = await PlotlyLib.toImage(gd, {
        format: 'png',
        height: 600,
        width: gd.offsetWidth || 1500,
        scale: 2,
      });

      // Create a sanitized filename from the title
      const sanitizedTitle = currentTitle.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 50);
      const filename = `${sanitizedTitle || 'chart'}.png`;

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading Plotly chart image:', error);
    }
  };

  const handleDownloadMatplotlib = () => {
    // Future‑ready: when backend returns image payloads (URL or data URI) in chart.data,
    // this function will trigger a download.
    try {
      if (!chart.data) {
        console.warn('No Matplotlib image data available for download yet');
        return;
      }

      let href = chart.data;

      // If the backend returns a raw base64 string without data URL prefix, normalise it.
      if (!href.startsWith('http') && !href.startsWith('data:')) {
        href = `data:image/png;base64,${href}`;
      }

      const link = document.createElement('a');
      link.href = href;
      link.download = 'chart.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error preparing Matplotlib chart download:', error);
    }
  };

  const handleSharePlotly = async () => {
    if (isSharing) return;
    setIsSharing(true);

    try {
      // Wait for chart to be fully rendered with retry logic
      const gd = await waitForPlotlyRef();
      if (!gd) {
        toast.error('Chart not ready. Please try again.');
        setIsSharing(false);
        return;
      }

      const PlotlyLib = (window as any).Plotly;
      if (!PlotlyLib || typeof PlotlyLib.toImage !== 'function') {
        toast.error('Unable to capture chart image');
        setIsSharing(false);
        return;
      }

      // Reset to original view before capturing (title is already visible in chart)
      await PlotlyLib.relayout(gd, {
        'xaxis.autorange': true,
        'yaxis.autorange': true,
      });

      // Wait for relayout to complete rendering
      await new Promise(resolve => setTimeout(resolve, 150));

      // Generate PNG image data URL
      const dataUrl = await PlotlyLib.toImage(gd, {
        format: 'png',
        height: 600,
        width: gd.offsetWidth || 1500,
        scale: 2,
      });

      // Convert data URL to Blob
      const imageBlob = dataURLtoBlob(dataUrl);

      // Try to use modern Clipboard API with ClipboardItem
      if (isClipboardItemSupported()) {
        try {
          // Create HTML content with summary text and embedded image
          // This allows pasting both text and image together in rich text editors like Teams
          const summaryHtml = summaryText && summaryText.trim()
            ? `<p>${summaryText.replace(/\n/g, '<br>')}</p>`
            : '';
          const htmlContent = `${summaryHtml}<img src="${dataUrl}" alt="Chart" style="max-width: 100%;"/>`;
          const htmlBlob = new Blob([htmlContent], { type: 'text/html' });

          // Also include plain text and image as separate formats for flexibility
          const plainText = summaryText || '';
          const textBlob = new Blob([plainText], { type: 'text/plain' });

          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': htmlBlob,
              'text/plain': textBlob,
              'image/png': imageBlob
            })
          ]);


        } catch (clipboardError) {
          console.error('Clipboard write failed:', clipboardError);
          // Fall back to download method
          handleFallbackShare(dataUrl);
        }
      } else {
        // Browser doesn't support ClipboardItem - use fallback
        handleFallbackShare(dataUrl);
      }
    } catch (error) {
      console.error('Error sharing Plotly chart:', error);
      toast.error('Failed to share chart. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  // Fallback for browsers that don't support ClipboardItem
  const handleFallbackShare = (dataUrl: string) => {
    // Copy summary text to clipboard if available
    if (summaryText && summaryText.trim()) {
      navigator.clipboard.writeText(summaryText).catch(() => { });
    }

    // Download the image
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `chart-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show toast first, then open Teams
    toast.info('Chart downloaded!', {
      description: 'Opening Microsoft Teams... Drag the image into the chat window.',
      duration: 6000,
    });

    // Delay opening Teams
    setTimeout(() => {
      const teamsUrl = 'https://teams.microsoft.com/l/chat/0/0';
      window.open(teamsUrl, '_blank');
    }, 1500);
  };

  const renderChart = () => {
    if (chart.type === 'plotly') {
      try {
        const parsed = JSON.parse(chart.data); // Expecting backend sends Plotly JSON
        applyBrandColorsToFigure(parsed);
        const existingLayout = parsed.layout || {};
        const existingX = existingLayout.xaxis || {};
        const existingY = existingLayout.yaxis || {};

        const getBold = (t?: any) => {
          const text = typeof t === 'string' ? t : (t && t.text) ? t.text : undefined;
          if (!text) return undefined;
          // Keep it simple: wrap in <b>..</b> if not already bold
          return /<\s*b\s*>/i.test(text) ? text : `<b>${text}</b>`;
        };

        const xTitle = getBold(existingX.title);
        const yTitle = getBold(existingY.title);

        // Enhanced data processing for better tooltips
        const addLineIndicatorToHoverTemplate = (template: string) => {
          // For line charts: add a small colored line indicator before the series name
          // to make it easier to match tooltip ↔ legend color.
          if (!template) return template;
          if (template.includes('data-legend-indicator="line"')) return template;

          const indicator =
            '<span data-legend-indicator="line" ' +
            'style="display:inline-block;width:18px;height:0;border-top:3px solid %{fullData.line.color};' +
            'border-radius:2px;margin-right:6px;vertical-align:middle;"></span>';

          const boldName = '<b>%{fullData.name}</b>';
          if (template.includes(boldName)) {
            return template.replace(boldName, `${indicator}${boldName}`);
          }
          if (template.includes('%{fullData.name}')) {
            return template.replace('%{fullData.name}', `${indicator}%{fullData.name}`);
          }

          // Fallback: prepend a header line if the template doesn't reference the name.
          return `${indicator}<b>%{fullData.name}</b><br>${template}`;
        };

        const enhancedData = parsed.data.map((trace: any) => {
          const isLineTrace =
            (trace?.type === 'scatter' || trace?.type === 'scattergl') &&
            String(trace?.mode || '').includes('lines');

          const baseTemplate =
            trace.hovertemplate ||
            '<b>%{fullData.name}</b><br>' +
            (existingX.title?.text || 'X') + ': <b>%{x}</b><br>' +
            (existingY.title?.text || 'Y') + ': <b>%{y}</b><br>' +
            '<extra></extra>';

          return {
            ...trace,
            hovertemplate: isLineTrace ? addLineIndicatorToHoverTemplate(baseTemplate) : baseTemplate,
            hoverlabel: {
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              bordercolor: '#f1f5f9',
              font: {
                color: '#111827',
                size: 14,
                family: 'HeinekenCore, Inter, system-ui, sans-serif'
              },
              namelength: -1
            }
          };
        });

        const isLineChart = enhancedData.length > 0 && enhancedData.every(
          (t: any) => (t.type === 'scatter' || t.type === 'scattergl') && String(t.mode || '').includes('lines')
        );
        const isBarChart = enhancedData.length > 0 && enhancedData.some((t: any) => t.type === 'bar');
        // Allow color selection for more Plotly traces (bar, pie, bubble/scatter markers, heatmap, etc.)
        const selectableChart = enhancedData.length > 0 && enhancedData.some((t: any) => {
          const caps = getTraceColorCaps(t);
          return Array.isArray(caps.modes) && caps.modes.length > 0;
        });

        // Apply heatmap colorscales via layout.coloraxis when a trace uses a shared coloraxis
        const layoutWithOverrides = (() => {
          const nextLayout = { ...existingLayout } as any;
          Object.entries(traceColorOverrides).forEach(([k, ov]) => {
            const i = Number(k);
            const t = enhancedData?.[i];
            if (!t || t.type !== 'heatmap' || !ov?.baseHex) return;
            const axisName = typeof t.coloraxis === 'string' ? t.coloraxis : null;
            if (!axisName) return;
            nextLayout[axisName] = { ...(nextLayout[axisName] || {}), colorscale: buildColorscale(ov.baseHex) };
          });
          return nextLayout;
        })();

        // Apply per-trace color overrides
        const dataWithOverrides = enhancedData.map((trace: any, i: number) => {
          const override = traceColorOverrides[i];
          const afterTrace = override ? applyTraceOverride(trace, override) : trace;

          // Apply per-point overrides (bars, bubbles/markers, pie slices)
          const perPoint = pointColorOverrides[i];
          if (!perPoint || Object.keys(perPoint).length === 0) return afterTrace;

          const caps = getTraceColorCaps(afterTrace);
          const n = caps.pointCount ?? 0;
          if (n <= 0) return afterTrace;

          const next = { ...afterTrace };
          const type = next?.type ?? 'scatter';

          // Ensure we have a mutable colors array for the correct property
          const ensureArray = (existing: any): string[] => {
            if (Array.isArray(existing) && existing.length > 0) {
              return existing.map((x) => String(x));
            }
            if (typeof existing === 'string' && existing) {
              return Array.from({ length: n }, () => existing);
            }
            // Fallback: generate a professional palette from the current picker base
            return buildPointColors(chartPaletteColors[0], n);
          };

          if (type === 'pie') {
            next.marker = next.marker || {};
            const arr = ensureArray(next.marker.colors);
            Object.entries(perPoint).forEach(([k, hex]) => {
              const idx = Number(k);
              if (Number.isFinite(idx) && idx >= 0 && idx < arr.length) arr[idx] = hex;
            });
            next.marker.colors = arr;
            return next;
          }

          next.marker = next.marker || {};
          const arr = ensureArray(next.marker.color);
          Object.entries(perPoint).forEach(([k, hex]) => {
            const idx = Number(k);
            if (Number.isFinite(idx) && idx >= 0 && idx < arr.length) arr[idx] = hex;
          });
          next.marker.color = arr;
          return next;
        });

        const displayData = isLineChart
          ? dataWithOverrides.map((trace: any, i: number) => {
            const selected = selectedTraceIndices.includes(i);
            const hasSelection = selectedTraceIndices.length > 0;
            const faded = hasSelection && !selected;
            return {
              ...trace,
              line: {
                ...trace.line,
                width: selected ? 2 : faded ? 1.5 : trace.line?.width,
              },
              opacity: selected ? 1 : faded ? 0.2 : 1,
            };
          })
          : isBarChart
            ? dataWithOverrides.map((trace: any, i: number) => {
              const selected = selectedTraceIndices.includes(i);
              const hasSelection = selectedTraceIndices.length > 0;
              const next: any = {
                ...trace,
                opacity: hasSelection ? (selected ? 1 : 0.45) : 1,
              };

              // Point highlight for bars: fade other bars when a single point is selected
              const selPoints = selectedPoints.filter((p) => p.traceIndex === i).map((p) => p.pointIndex);
              if (selPoints.length > 0 && trace?.type === 'bar') {
                const caps = getTraceColorCaps(trace);
                const n = caps.pointCount ?? 0;
                if (n > 0) {
                  next.marker = next.marker || {};
                  const set = new Set(selPoints);
                  next.marker.opacity = Array.from({ length: n }, (_, j) => (set.has(j) ? 1 : 0.25));
                }
              }

              return next;
            })
            : dataWithOverrides;

        // Point highlight for bubble/scatter markers (and other marker-based traces)
        const displayDataWithPointHighlight = (() => {
          if (selectedPoints.length === 0) return displayData;
          return (displayData as any[]).map((trace: any, i: number) => {
            const selPoints = selectedPoints.filter((p) => p.traceIndex === i).map((p) => p.pointIndex);
            if (selPoints.length === 0) return trace;
            const type = trace?.type ?? 'scatter';
            const mode = String(trace?.mode ?? '');
            if (!((type === 'scatter' || type === 'scattergl') && mode.includes('markers'))) return trace;

            const caps = getTraceColorCaps(trace);
            const n = caps.pointCount ?? 0;
            if (n <= 0) return trace;

            const next: any = { ...trace };
            next.marker = next.marker || {};
            const set = new Set(selPoints);
            next.marker.opacity = Array.from({ length: n }, (_, j) => (set.has(j) ? 1 : 0.25));
            return next;
          });
        })();

        return (
          <div
            className={`${isMobile ? 'overflow-x-auto scroll-smooth' : ''}`}
            style={isMobile ? { maxWidth: mobileChartContainer } : undefined}
          >
            <div className="relative w-full group" style={isMobile ? { minWidth: mobileChartMinWidth } : undefined}>
              {/* Editable Title Input - appears when editing */}
              {isEditingTitle && (
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20">
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    onBlur={handleSaveTitle}
                    className={`px-3 py-1.5 text-lg font-semibold text-center border ${colors.border.primary} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-lg`}
                    style={{ width: '450px' }}
                    placeholder="Enter chart title..."
                  />
                </div>
              )}

              {/* Action buttons (top-right) */}
              <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1.5">
                {/* Color picker: only when at least one trace is selected */}
                {(selectedTraceIndices.length > 0 || selectedPoints.length > 0) && (
                  <div className="relative" ref={colorPickerRef}>
                    <button
                      type="button"
                      onClick={() => {
                        const next = !isColorPickerOpen;
                        if (next) {
                          const pt = selectedPoints[0];
                          if (pt) {
                            const existing = pointColorOverrides?.[pt.traceIndex]?.[pt.pointIndex];
                            setCustomPickerHex(existing ?? '#13670B');
                          } else if (selectedTraceIndices[0] != null) {
                            setCustomPickerHex(traceColorOverrides[selectedTraceIndices[0]]?.baseHex ?? '#13670B');
                          }
                        }
                        setIsColorPickerOpen(next);
                      }}
                      className={`rounded-md p-1.5 border ${colors.border.primary} ${colors.bg.secondary} shadow-md hover:bg-gray-50`}
                      aria-label="Change trace color"
                      title="Change color"
                    >
                      <Palette className="w-4 h-4" />
                    </button>
                    {isColorPickerOpen && (
                      <div
                        className={`absolute right-0 top-full mt-1.5 z-30 rounded-lg border ${colors.border.primary} ${colors.bg.secondary} shadow-lg p-3 min-w-[180px]`}
                      >
                        <p className={`text-xs font-medium ${colors.text.secondary} mb-2`}>Heineken Color</p>
                        <div className="grid grid-cols-4 gap-1.5 mb-3">
                          {chartPaletteColors.map((hex) => (
                            <button
                              key={hex}
                              type="button"
                              onClick={() => {
                                applyColorToSelection(hex);
                              }}
                              onMouseEnter={() => setPickerPreviewHex(hex)}
                              onMouseLeave={() => setPickerPreviewHex(null)}
                              className="w-8 h-8 rounded border border-gray-300 hover:ring-2 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                              style={{ backgroundColor: hex }}
                              title={hex}
                            />
                          ))}
                        </div>

                        {/* Unified preview + hex input */}
                        <div className="flex items-center justify-between gap-2 mb-3 w-full">
                          <input
                            value={customPickerHex}
                            onChange={(e) => {
                              const next = normalizeHexInput(e.target.value);
                              setCustomPickerHex(next);
                              if (isValidHex(next)) applyColorToSelection(next);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const next = normalizeHexInput((e.target as HTMLInputElement).value);
                                if (isValidHex(next)) applyColorToSelection(next);
                              }
                            }}
                            placeholder="#13670B"
                            spellCheck={false}
                            className={`w-full px-2 py-1 rounded-md border ${colors.border.primary} text-black text-md focus:outline-none focus:ring-2 focus:ring-blue-400`}
                            aria-label="Hex color"
                            title="Hex color"
                          />
                        </div>

                        <p className={`text-xs font-medium ${colors.text.secondary} mb-2`}>Color picker</p>
                        <div className="mb-2">
                          <HexColorPicker
                            color={customPickerHex}
                            onChange={(hex) => {
                              applyColorToSelection(hex);
                            }}
                            style={{ width: '100%', height: 120 }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Edit title button */}
                <button
                  type="button"
                  onClick={handleStartEditTitle}
                  className={`rounded-md p-1.5 border ${colors.border.primary} ${colors.bg.secondary} shadow-md hover:bg-gray-50`}
                  aria-label="Edit chart title"
                  title="Edit title"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {/* Download button */}
                <button
                  type="button"
                  onClick={handleDownloadPlotly}
                  className={`rounded-md p-1.5 border ${colors.border.primary} ${colors.bg.secondary} shadow-md hover:bg-gray-50`}
                  aria-label="Download chart as image"
                  title="Download as PNG"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleSharePlotly}
                  disabled={isSharing}
                  className={`rounded-md p-1.5 border ${colors.border.primary} ${colors.bg.secondary} shadow-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-wait`}
                  aria-label="Share chart to Microsoft Teams"
                  title="Copy to clipboard"
                >
                  {isSharing ? (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div style={{ height: 500, overflow: 'hidden', borderRadius: 12 }}>
                <Plot
                  data={displayDataWithPointHighlight}
                  layout={{
                    ...layoutWithOverrides,
                    uirevision: chart.chart_id,
                    height: 500,
                    margin: { ...baseMargins, ...(existingLayout.margin || {}) },
                    // Use a solid white background so exported images look clean and professional,
                    // without dark/black borders from transparent regions.
                    paper_bgcolor: '#ffffff',
                    plot_bgcolor: '#ffffff',
                    font: {
                      color: '#374151',
                      family: 'HeinekenCore, Inter, system-ui, sans-serif',
                      size: 14
                    },
                    title: {
                      text: currentTitle,
                      font: {
                        size: 18,
                        color: '#111827',
                        family: 'HeinekenCore, Inter, system-ui, sans-serif'
                      },
                      x: 0.5,
                      xanchor: 'center'
                    },
                    xaxis: {
                      ...existingX,
                      showgrid: true,
                      gridcolor: '#e5e7eb',
                      gridwidth: 1,
                      zeroline: false,
                      showline: true,
                      linecolor: '#d1d5db',
                      linewidth: 1,
                      automargin: true,
                      tickangle: -45,
                      tickfont: {
                        size: 11,
                        color: '#6b7280',
                        family: 'HeinekenCore, Inter, system-ui, sans-serif'
                      },
                      title: {
                        ...(typeof existingX.title === 'object' ? existingX.title : {}),
                        text: xTitle ?? existingX.title?.text ?? existingX.title ?? undefined,
                        font: {
                          ...(existingX.title?.font || {}),
                          size: 14,
                          color: '#374151',
                          family: 'HeinekenCore, Inter, system-ui, sans-serif'
                        },
                        standoff: 20,
                      },
                    },
                    yaxis: {
                      ...existingY,
                      showgrid: true,
                      linecolor: '#d1d5db',
                      linewidth: 1,
                      automargin: true,
                      tickfont: {
                        size: 11,
                        color: '#6b7280',
                        family: 'HeinekenCore, Inter, system-ui, sans-serif'
                      },
                      title: {
                        ...(typeof existingY.title === 'object' ? existingY.title : {}),
                        text: yTitle ?? existingY.title?.text ?? existingY.title ?? undefined,
                        font: {
                          ...(existingY.title?.font || {}),
                          size: 14,
                          color: '#374151',
                          family: 'HeinekenCore, Inter, system-ui, sans-serif'
                        },
                        standoff: 25,
                      },
                    },
                    legend: {
                      // line below would make the title of legend appear, comment to hide it
                      // ...existingLayout.legend || {},
                      font: {
                        size: 12,
                        color: '#374151',
                        family: 'HeinekenCore, Inter, system-ui, sans-serif'
                      },
                    },
                    autosize: true,
                    responsive: true,
                    dragmode: 'pan',
                    hovermode: 'closest',
                  }}
                  config={{
                    displayModeBar: false,
                    responsive: true,
                    scrollZoom: true,
                    showTips: false,
                  }}
                  style={{ width: '100%', height: 500 }}
                  onClick={(event) => {
                    if (selectableChart && event?.points?.[0] != null) {
                      const curve = event.points[0].curveNumber;
                      const pointNumber = event.points[0].pointNumber;
                      const clickedTrace = enhancedData?.[curve];
                      const caps = getTraceColorCaps(clickedTrace);
                      const supportsPoints = caps.modes.includes('points') && typeof pointNumber === 'number';

                      if (supportsPoints) {
                        // Point selection (bars/bubbles/pie slices)
                        setSelectedTraceIndices([]);
                        setSelectedPoints((prev) => {
                          const exists = prev.some((p) => p.traceIndex === curve && p.pointIndex === pointNumber);
                          if (exists) {
                            // Toggle off if clicked again
                            return prev.filter((p) => !(p.traceIndex === curve && p.pointIndex === pointNumber));
                          }
                          // Multi-select: add new point
                          return [...prev, { traceIndex: curve, pointIndex: pointNumber }];
                        });
                        return;
                      }

                      // Trace selection (lines and other trace-wide styles)
                      setSelectedPoints([]);
                      setSelectedTraceIndices((prev) => {
                        const clickedType = clickedTrace?.type ?? 'scatter';
                        const singleSelect = clickedType === 'bar' || clickedType === 'pie' || clickedType === 'heatmap';

                        if (singleSelect || isBarChart) {
                          if (prev.includes(curve) && prev.length === 1) return [];
                          return [curve];
                        }

                        if (prev.includes(curve)) return prev.filter((x) => x !== curve);
                        return [...prev, curve];
                      });
                    }
                  }}
                  onDoubleClick={() => {
                    if (selectableChart) {
                      setSelectedTraceIndices([]);
                      setSelectedPoints([]);
                    }
                  }}
                  onInitialized={(_, graphDiv) => {
                    plotlyGraphRef.current = graphDiv;
                  }}
                  onUpdate={(_, graphDiv) => {
                    plotlyGraphRef.current = graphDiv;
                  }}
                />
              </div>
            </div>
          </div>
        );
      } catch (error) {
        console.error('Error parsing Plotly chart data:', error);
        return (
          <div className={`${colors.bg.tertiary} rounded-lg p-8 text-center border ${colors.border.primary}`}>
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className={`${colors.text.primary} font-medium`}>Error loading chart</p>
              <p className={`${colors.text.secondary} text-sm`}>Please try again or contact support</p>
            </div>
          </div>
        );
      }
    } else {
      // Matplotlib charts
      return (
        <div
          className={`${isMobile ? 'overflow-x-auto scroll-smooth' : ''}`}
          style={isMobile ? { maxWidth: mobileChartContainer } : undefined}
        >
          <div className="relative" style={isMobile ? { minWidth: mobileChartMinWidth } : undefined}>
            {/* Download button overlay (top-right) */}
            <button
              type="button"
              onClick={handleDownloadMatplotlib}
              className={`absolute top-2 right-2 z-10 rounded-md p-1.5 border ${colors.border.primary} ${colors.bg.secondary} shadow-md hover:bg-gray-50`}
              aria-label="Download chart as image"
            >
              <Download className="w-4 h-4" />
            </button>

            <div
              className={`${colors.bg.tertiary} rounded-lg p-8 text-center border ${colors.border.primary}`}
            >
              <div className="flex flex-col items-center gap-2">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className={`${colors.text.primary} font-medium`}>Matplotlib Chart: {chart.title}</p>
                <p className={`${colors.text.secondary} text-sm`}>
                  Chart visualization would be rendered here
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className={`${colors.bg.secondary} rounded-xl border ${colors.border.primary} p-6 mb-4 shadow-sm hover:shadow-md transition-shadow duration-200`}>
      {renderChart()}
    </div>
  );
};

interface DataDisplayProps {
  data: {
    tables: TableData[];
    charts: ChartData[];
    chat_responses: Array<{
      title: string;
      content: string;
    }>;
  };
  summaryText?: string;
}

export const DataDisplay: React.FC<DataDisplayProps> = ({ data, summaryText }) => {
  return (
    <div className="space-y-4">
      {/* Render tables */}
      {data.tables.map((table, index) => (
        <DataTable key={`table-${index}`} table={table} />
      ))}

      {/* Render charts */}
      {data.charts.map((chart, index) => (
        <DataChart key={`chart-${index}`} chart={chart} summaryText={summaryText} />
      ))}

      {/* Render additional chat responses */}
      {data.chat_responses.map((response, index) => (
        <div key={`chat-${index}`} className="mb-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
              {response.title}
            </h4>
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              {response.content}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};
